import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CheckIcon } from "../viewer/viewer-icons";

// Gap between two sided grids (dashboard-view uses gap: 2 = 16px).
const GRID_GAP = 16;
const gridSize = (vhUnit) => `min(40vw, 22${vhUnit})`;
// A wide grid spans the same width as two sided grids plus their gap.
const wideGridWidth = (vhUnit) =>
  `calc(${gridSize(vhUnit)} * 2 + ${GRID_GAP}px)`;

// Viewfinder corner brackets drawn around a framed grid placeholder.
const ViewfinderFrame = ({ color }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1}
    strokeLinecap="round"
    style={{ position: "absolute", width: "72%", height: "72%" }}
  >
    <path d="M3.5 8V5.5C3.5 4.4 4.4 3.5 5.5 3.5H8" />
    <path d="M16 3.5h2.5c1.1 0 2 .9 2 2V8" />
    <path d="M20.5 16v2.5c0 1.1-.9 2-2 2H16" />
    <path d="M8 20.5H5.5c-1.1 0-2-.9-2-2V16" />
  </svg>
);

ViewfinderFrame.propTypes = {
  color: PropTypes.string.isRequired,
};

// One dashboard grid: a light-grey rounded box holding the placeholder icon
// on a white shadowed circle, with a label underneath.
// framed:    the icon sits inside viewfinder corner brackets (scan grids).
// wide:      a rectangle as wide as two sided grids (non-sided and image grids).
// done:      the grid's data has been captured — normally indicated by its
//            thumbnail; a check badge is the fallback when there is none.
// thumbnail: captured model/photo image, shown centered and in full on the
//            tile instead of the placeholder circle.
// padded:    inset the thumbnail from the tile's top and bottom edges (used
//            for photos, which would otherwise touch them).
export const DashboardGrid = (props) => {
  const {
    label,
    framed,
    wide,
    done,
    thumbnail,
    padded,
    active = true,
    onClick,
    children,
  } = props;
  const theme = useTheme();
  const tint = theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.75,
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label={label}
        disabled={!active}
        onClick={onClick}
        sx={{
          width: wide ? wideGridWidth("vh") : gridSize("vh"),
          height: gridSize("vh"),
          "@supports (height: 1svh)": {
            width: wide ? wideGridWidth("svh") : gridSize("svh"),
            height: gridSize("svh"),
          },
          border: "none",
          borderRadius: "8px",
          p: 0,
          backgroundColor: "#EBEBEB",
          opacity: active ? 1 : 0.4,
          cursor: active ? "pointer" : "default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          color: tint,
        }}
      >
        {thumbnail ? (
          // the captured image, shown in full and centered on the tile (it
          // fills square tiles; on wide tiles it sits in the middle)
          <Box
            component="img"
            src={thumbnail}
            alt={label}
            sx={{
              height: "100%",
              maxWidth: "100%",
              objectFit: "contain",
              py: padded ? 1.5 : 0,
              boxSizing: "border-box",
            }}
          />
        ) : (
          <Box
            sx={{
              height: "72%",
              aspectRatio: "1 / 1",
              borderRadius: "50%",
              backgroundColor: "#fff",
              boxShadow: "0 3px 10px rgba(0,0,0,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {framed && <ViewfinderFrame color={tint} />}
            {children}
          </Box>
        )}
        {done && !thumbnail && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: tint,
              boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckIcon size={18} color="#fff" />
          </Box>
        )}
      </Box>
      <Typography fontSize={16} color="text.primary">
        {label}
      </Typography>
    </Box>
  );
};

DashboardGrid.propTypes = {
  label: PropTypes.string.isRequired,
  framed: PropTypes.bool,
  wide: PropTypes.bool,
  done: PropTypes.bool,
  thumbnail: PropTypes.string,
  padded: PropTypes.bool,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
};
